import { vector3 } from '@/domain/geometry/vector3';
import { findPlaneMeasurement, type MeasurementGroup } from '@/domain/anatomy/measurements';
import { LpsCoordinateSystem, lpsIdentityCS } from '@/domain/anatomy/LPS';

/**
 * Alignment mode (coordinate systems) for measurements related to the pelvis.
 *
 * - 'CT' is the CT coordinate system
 * - 'APP' is the APP coordinate system, which is defined relative to the anterior-pelvic-plane
 * - 'FPP' is the FPP coordinate system, which is defined relative to the functional-pelvic-plane
 */
export enum AlignmentMode {
    CT = 'CT',
    APP = 'APP',
    FPP = 'FPP',
}

export enum TranslationMode {
    ACETABULAR = 'Acetabular',
    CT = 'CT',
    APP = 'APP',
    FPP = 'FPP',
}

export function alignmentModeToTranslationMode(alignmentMode?: AlignmentMode): TranslationMode {
    switch (alignmentMode) {
        case AlignmentMode.CT:
            return TranslationMode.CT;
        case AlignmentMode.APP:
            return TranslationMode.APP;
        case AlignmentMode.FPP:
            return TranslationMode.FPP;
        default:
            return TranslationMode.CT;
    }
}

const enum PlaneTypes {
    ANTERIOR = 'anterior_pelvic_plane',
    FUNCTIONAL = 'functional_pelvic_plane',
}

/**
 * Get a set of LPS vectors for the given alignment mode
 */
export function alignmentCS(
    alignmentMode: AlignmentMode,
    measurements: MeasurementGroup[],
): LpsCoordinateSystem {
    switch (alignmentMode) {
        case AlignmentMode.CT:
            return lpsIdentityCS();
        case AlignmentMode.APP:
            return appCS(measurements);
        case AlignmentMode.FPP:
            return fppCS(measurements);
        default:
            throw new Error(`Invalid alignment mode ${alignmentMode}`);
    }
}

function createPlaneMeasurementCS(
    measurements: MeasurementGroup[],
    planeType: PlaneTypes,
): LpsCoordinateSystem {
    const plane = findPlaneMeasurement(measurements, 'pelvis', `hip_pelvis_${planeType}`);

    const right = vector3(plane.x);
    const anterior = vector3(plane.value);
    const superior = vector3(plane.y);

    const left = right.negate().addScalar(0);
    const posterior = anterior.negate().addScalar(0);
    return LpsCoordinateSystem.make(left, posterior, superior);
}

/**
 * Create a set of direction-vectors representing the APP coordinate system, which is defined
 * relative to the anterior-pelvic-plane
 * */
function appCS(measurements: MeasurementGroup[]): LpsCoordinateSystem {
    return createPlaneMeasurementCS(measurements, PlaneTypes.ANTERIOR);
}

/**
 * Create a set of direction-vectors representing the FPP coordinate system, which is defined
 * relative to the functional-pelvic-plane
 * */
function fppCS(measurements: MeasurementGroup[]): LpsCoordinateSystem {
    return createPlaneMeasurementCS(measurements, PlaneTypes.FUNCTIONAL);
}
